import React, { useEffect, useState } from 'react';
import { apiClient } from '../utils/api';
import {
  PieChart, Pie, Cell, Tooltip as ReTooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend,
} from 'recharts';
import { AnimatePresence, motion } from 'framer-motion';

const YES_COLOR = '#059669';   // Emerald-600
const NO_COLOR = '#DC2626';    // Red-600
const MALE_COLOR = '#2563EB';  // Blue-600
const FEMALE_COLOR = '#DB2777';// Pink-600

const sections = ['Morning', 'Afternoon', 'Counting'] as const;
type Section = typeof sections[number];

// Utility function to format numbers professionally (no rounding up)
const formatNumber = (num: number): string => {
  const floorToDecimal = (value: number, decimals: number) => {
    const factor = Math.pow(10, decimals);
    return Math.floor(value * factor) / factor;
  };

  if (num >= 1_000_000) {
    const v = floorToDecimal(num / 1_000_000, 1);
    return v.toFixed(1).replace(/\.0$/, '') + 'M';
  }
  if (num >= 1_000) {
    const v = floorToDecimal(num / 1_000, 1);
    return v.toFixed(1).replace(/\.0$/, '') + 'K';
  }
  return num.toString();
};

// Utility function to get full number with commas
const formatNumberWithCommas = (num: number): string => {
  return num.toLocaleString();
};

// Types
type SummaryQuestion = {
  question: string;
  yes: number;
  no: number;
  male?: number;
  female?: number;
};

type SummaryData = {
  reported_stations: number;
  timeline: string;
  questions: SummaryQuestion[];
};

type PieSlice = { name: string; value: number; color: string };
type PieChartConfig = { title: string; data: PieSlice[]; mode: 'yesno' | 'gender' };

type ExtraData = {
  delay_reasons: { reason: string; count: number }[];
  resources: Record<string, number>;
  incidents_over_time: { hour: string; incidents: number }[];
  observer_compliance: { district: string; compliance: number }[];
} | null;

const GROUP_MAP: Record<string, string> = {
  // Morning & Afternoon grouped consistently
  'Polling Opened On Time': 'Polling Setup',
  'MEC Officials present': 'Polling Setup',
  'Party Representatives Present': 'Polling Setup',
  'Security Personnel Present': 'Polling Setup',
  'Party Reps Present': 'Polling Setup',
  'Security Maintained Order': 'Security & Compliance',
  'Safe & Accessible for Women': 'Polling Setup',
  'Ballot papers opened with  all Officials ': 'Polling Setup',
  'Ballots Opened with Officials': 'Voting Process',
  'Voting Disrupted by Malfunctions': 'Voting Process',
  'VRC Checked Before Voting': 'Voting Process',
  'No VRC, No Vote': 'Voting Process',
  'Voters from Other Stations Voted': 'Voting Process',
  'Names Ticked on Voter List': 'Voting Process',
  'Names Called Out Loud': 'Voting Process',
  'Voting Process Explained': 'Voting Process',
  'Location safe for women': 'Polling Setup',
  'Privacy During Voting Ensured': 'Voting Process',
  'Campaigning at Polling Station': 'Security & Compliance',
  'Vote Buying at Station': 'Security & Compliance',
  'VRC Card Embossed After Voting': 'Voting Process',
  'Attempted Double Voting': 'Security & Compliance',
  'Replacement Ballots Given': 'Voting Process',
  'Spoiled Ballots Surrendered': 'Voting Process',
  'Ink Checked Before Voting': 'Voting Process',
  'Ink Applied After Voting': 'Voting Process',
  'Voting Queue Moved Smoothly (AM)': 'Polling Setup',
  'Polling Centre Spacious Enough': 'Polling Setup',
  'Polling Centre Properly Shaded': 'Polling Setup',
  'Health Safety Measures Present': 'Polling Setup', // fixed key to match backend label
  'PWDs Given Priority Access': 'Voting Process',
  'Pregnant Women Given Priority': 'Voting Process',
  'Women with Kids Given Priority': 'Voting Process',
  'Elderly Given Priority': 'Voting Process',
  'Party Reps Observed Voting Fully': 'Security & Compliance',
  'CSO Observers Active': 'Security & Compliance',
  'Complaints Lodged at Station': 'Security & Compliance',

  // Afternoon
  'Polling Centre Still Open': 'Polling Setup',
  'Long Queue in Afternoon': 'Polling Setup',
  'Party Reps Allowed Duties': 'Security & Compliance',
  'Safe for Women': 'Polling Setup',
  'Campaigning Around Centre': 'Security & Compliance',
  'Equipment Malfunctions Observed': 'Voting Process',
  'Out-of-Centre Voters Allowed': 'Voting Process',
  'Queue Moved Smoothly (PM)': 'Polling Setup',
  'Centre Spacious': 'Polling Setup',
  'Centre Protected from Weather': 'Polling Setup',
  'Women w/ Children Priority': 'Voting Process',
  'Elderly Priority Access': 'Voting Process',
  'CSOs Observed Voting': 'Security & Compliance',
  'Complaints Lodged': 'Security & Compliance',
  'Voter Import from Other Areas': 'Security & Compliance',

  // Counting
  'Were ballots counted at the polling station?': 'Counting Process',
  'Was lighting sufficient during the counting process?': 'Counting Process',
  'Were ballots transported elsewhere for counting?': 'Counting Process',
  'Were party representatives present during counting?': 'Counting Process',
  'Were observers present and able to monitor the count?': 'Counting Process',
  'Did you successfully record and submit vote data on Maso Athu?': 'Counting Process',
  'Were school facilities used during the election left in their original condition?': 'Polling Environment',
};

const EdayPageGraphs: React.FC = () => {
  const [activeSection, setActiveSection] = useState<Section>('Morning');
  const [summaryData, setSummaryData] = useState<SummaryData | null>(null);
  const [extraData, setExtraData] = useState<ExtraData>(null);
  const [viewMode, setViewMode] = useState<'pie' | 'bar'>('pie');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const timeline = activeSection.toLowerCase();
    const needsExtra = activeSection === 'Morning';

    setIsLoading(true);

    (async () => {
      const requests = [
        apiClient.get(`/eday/eday-summary?timeline=${timeline}`),
        needsExtra
          ? apiClient.get(`/eday/extract?timeline=${timeline}`)
          : Promise.resolve({ data: { data: null } }),
      ];

      const [summaryRes, extractRes] = await Promise.allSettled(requests);

      if (summaryRes.status === 'fulfilled') {
        setSummaryData(summaryRes.value.data.data);
      } else {
        console.error('eday-summary failed:', summaryRes.reason);
        setSummaryData({ reported_stations: 0, timeline, questions: [] });
      }

      if (extractRes.status === 'fulfilled') {
        setExtraData(extractRes.value.data.data);
      } else {
        setExtraData(null);
      }

      setIsLoading(false);
    })();
  }, [activeSection]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 border-4 border-indigo-200 rounded-full animate-spin border-t-indigo-600"></div>
            <h2 className="mb-2 text-2xl font-bold text-gray-800">Loading E-Day Dashboard</h2>
            <p className="text-gray-600">Fetching real-time election data...</p>
          </div>
        </div>
      </div>
    );
  }

  const needsExtra = activeSection === 'Morning';
  if (!summaryData || (needsExtra && !extraData)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="flex items-center justify-center min-h-screen">
          <div className="max-w-md p-8 mx-4 bg-white shadow-2xl rounded-2xl">
            <div className="mb-6 text-center">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="mb-2 text-2xl font-bold text-gray-800">E-Day Dashboard</h2>
              <p className="mb-6 text-gray-600">
                No E-Day data available. This feature requires backend endpoints that may not be implemented yet.
              </p>
            </div>
            <div className="p-4 border border-blue-200 bg-blue-50 rounded-xl">
              <h3 className="flex items-center mb-3 font-semibold text-blue-800">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Required endpoints:
              </h3>
              <ul className="space-y-2 text-sm text-blue-700">
                <li className="flex items-center">
                  <span className="w-2 h-2 mr-3 bg-blue-400 rounded-full"></span>
                  <code className="px-2 py-1 bg-blue-100 rounded">/api/eday/eday-summary?timeline={`{timeline}`}</code>
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 mr-3 bg-blue-400 rounded-full"></span>
                  <code className="px-2 py-1 bg-blue-100 rounded">/api/eday/extract?timeline={`{timeline}`}</code>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Empty state if there are no questions for the selected timeline
  if (!summaryData.questions || summaryData.questions.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="max-w-lg p-8 text-center bg-white shadow-xl rounded-2xl">
          <h2 className="mb-2 text-2xl font-bold text-gray-800">
            No responses yet for {activeSection}.
          </h2>
          <p className="text-gray-600">
            We haven’t received any reports tagged with the “{activeSection.toLowerCase()}” timeline.
          </p>
        </div>
      </div>
    );
  }

  // Build per-question chart configs (auto gender vs yes/no)
  const pieCharts: PieChartConfig[] = (summaryData.questions || []).map((q) => {
    const male = q.male ?? 0;
    const female = q.female ?? 0;
    const hasGender = (male + female) > 0;

    if (hasGender) {
      return {
        title: q.question,
        mode: 'gender',
        data: [
          { name: 'Male', value: male, color: MALE_COLOR },
          { name: 'Female', value: female, color: FEMALE_COLOR },
        ],
      };
    }

    return {
      title: q.question,
      mode: 'yesno',
      data: [
        { name: 'Yes', value: q.yes, color: YES_COLOR },
        { name: 'No', value: q.no, color: NO_COLOR },
      ],
    };
  });

  // Group charts for layout
  const groupedCharts: Record<string, PieChartConfig[]> = {};
  pieCharts.forEach((chart) => {
    const group = GROUP_MAP[chart.title] || 'Other';
    if (!groupedCharts[group]) groupedCharts[group] = [];
    groupedCharts[group].push(chart);
  });

  // Morning resources bar data
  const resourceData = Object.entries((extraData?.resources || {})).map(([k, v]) => ({
    name: k.replace(/_/g, ' ').toUpperCase(),
    count: v as number,
  }));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 z-50 border-b bg-white/80 backdrop-blur-lg border-gray-200/50">
        <div className="px-6 py-8 mx-auto max-w-7xl">
          <div className="flex flex-col justify-between mb-8 lg:flex-row lg:items-center">
            <div className="mb-6 lg:mb-0">
              <div className="flex items-center mb-2 space-x-3">
                <div className="p-2 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h1 className="text-4xl font-bold text-transparent bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text">
                  E-Day Dashboard
                </h1>
              </div>
              <p className="text-lg font-medium text-gray-600">
                Real-time election monitoring & comprehensive reporting system
              </p>
            </div>

            <div className="flex flex-col gap-4 sm:flex-row">
              <div className="p-4 text-white shadow-lg bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl">
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-lg bg-white/20">
                    <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-green-100">System Status</p>
                    <p className="text-lg font-bold text-white">Live Data</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Timeline Switcher */}
          <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
            <div className="flex p-2 border bg-gray-100/50 backdrop-blur-sm rounded-2xl border-gray-200/50">
              {sections.map((section) => (
                <button
                  key={section}
                  onClick={() => setActiveSection(section)}
                  className={`relative px-8 py-4 text-sm font-semibold rounded-xl transition-all duration-300 ${activeSection === section
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg shadow-indigo-500/20'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                    }`}
                >
                  {activeSection === section && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute inset-0 bg-white/10 rounded-xl"
                      transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                  <span className="relative z-10">{section} Reporting</span>
                </button>
              ))}
            </div>

            <button
              onClick={() => setViewMode(viewMode === 'pie' ? 'bar' : 'pie')}
              className="px-8 py-4 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-2xl hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <span className="flex items-center space-x-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                </svg>
                <span>Switch to {viewMode === 'pie' ? 'Bar' : 'Donut'} View</span>
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-8 mx-auto max-w-7xl">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeSection}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-12"
          >
            {Object.entries(groupedCharts).map(([sectionTitle, charts]) => (
              <div key={sectionTitle} className="space-y-8">
                <div className="relative">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center justify-center w-12 h-12 shadow-lg bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-2xl font-bold text-gray-900">{sectionTitle}</h4>
                        <p className="font-medium text-gray-600">
                          {charts.length} questions monitored across polling stations
                        </p>
                      </div>
                    </div>
                    <div className="items-center hidden space-x-2 sm:flex">
                      <div className="px-4 py-2 text-sm font-semibold text-indigo-700 bg-indigo-100 rounded-xl">
                        {charts.length} Metrics
                      </div>
                    </div>
                  </div>
                  <div className="absolute left-0 w-24 h-1 rounded-full -bottom-2 bg-gradient-to-r from-indigo-500 to-purple-500"></div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                  {charts.map(({ title, data, mode }, i) => {
                    const total = data.reduce((sum, d) => sum + d.value, 0);
                    const a = data[0]; // first slice (Yes or Male)
                    const b = data[1]; // second slice (No or Female)
                    const aPercent = total > 0 ? Math.round((a.value / total) * 100) : 0;
                    const bPercent = total > 0 ? Math.round((b.value / total) * 100) : 0;

                    // Keep layout; just make labels/colors dynamic
                    const dotAClass =
                      mode === 'gender'
                        ? 'w-4 h-4 rounded-full shadow-sm bg-blue-600'
                        : 'w-4 h-4 rounded-full shadow-sm bg-gradient-to-r from-green-400 to-green-600';
                    const dotBClass =
                      mode === 'gender'
                        ? 'w-4 h-4 rounded-full shadow-sm bg-pink-600'
                        : 'w-4 h-4 rounded-full shadow-sm bg-gradient-to-r from-red-400 to-red-600';
                    const textAClass =
                      mode === 'gender' ? 'text-gray-700' : 'text-green-700';
                    const textBClass =
                      mode === 'gender' ? 'text-gray-700' : 'text-red-700';

                    return (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.05 }}
                        className="relative overflow-hidden transition-all duration-500 bg-white border shadow-lg group backdrop-blur-sm border-gray-200/70 rounded-2xl hover:shadow-2xl hover:-translate-y-1 hover:border-indigo-200"
                      >
                        <div className="absolute inset-0 transition-opacity duration-500 opacity-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5 rounded-2xl group-hover:opacity-100"></div>

                        {/* Professional status indicator */}
                        <div className="absolute z-10 top-4 right-4">
                          <div className="flex items-center space-x-1">
                            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                            <span className="hidden text-xs font-medium text-green-600 sm:inline">Live</span>
                          </div>
                        </div>

                        <div className="relative p-4 pb-3 sm:p-6">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1 pr-8">
                              <h3 className="mb-2 text-sm font-bold leading-tight text-gray-900 transition-colors duration-300 sm:text-base line-clamp-3 group-hover:text-indigo-700">
                                {title}
                              </h3>
                              <div className="flex items-center text-xs text-gray-500">
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                                <span className="font-medium">Real-time data</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="relative px-4 pb-4 sm:px-6">
                          <ResponsiveContainer width="100%" height={viewMode === 'pie' ? 220 : 140}>
                            {viewMode === 'pie' ? (
                              <PieChart>
                                <Pie
                                  data={[
                                    { name: a.name, value: a.value },
                                    { name: b.name, value: b.value },
                                  ]}
                                  dataKey="value"
                                  nameKey="name"
                                  cx="50%"
                                  cy="50%"
                                  outerRadius={70}
                                  innerRadius={40}
                                  strokeWidth={2}
                                  stroke="#fff"
                                >
                                  <Cell fill={a.color} />
                                  <Cell fill={b.color} />
                                </Pie>
                                <text x="50%" y="45%" textAnchor="middle" dominantBaseline="middle" fill="#1F2937" fontSize="18" fontWeight="bold">
                                  <title>{`Total: ${formatNumberWithCommas(total)}`}</title>
                                  {formatNumber(total)}
                                </text>
                                <text x="50%" y="55%" textAnchor="middle" dominantBaseline="middle" fill="#6B7280" fontSize="12" fontWeight={500}>
                                  Total
                                </text>
                                <ReTooltip
                                  formatter={(value: number, name: string) => [
                                    formatNumberWithCommas(value),
                                    `${name} ${mode === 'gender' ? '(Gender)' : '(Response)'}`
                                  ]}
                                  labelFormatter={() => ''}
                                  contentStyle={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '12px',
                                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                                    backdropFilter: 'blur(10px)',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                  }}
                                />
                              </PieChart>
                            ) : (
                              <BarChart data={[{ name: a.name, value: a.value }, { name: b.name, value: b.value }]} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                                <XAxis type="number" />
                                <YAxis type="category" dataKey="name" width={40} />
                                <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                                  <Cell fill={a.color} />
                                  <Cell fill={b.color} />
                                </Bar>
                                <ReTooltip
                                  formatter={(value: number) => [formatNumberWithCommas(value), '']}
                                  contentStyle={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '12px',
                                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                                    backdropFilter: 'blur(10px)'
                                  }}
                                />
                              </BarChart>
                            )}
                          </ResponsiveContainer>
                        </div>

                        <div className="relative px-3 py-3 border-t sm:px-4 sm:py-4 bg-gray-50/80 border-gray-200/50">
                          <div className="flex items-center justify-between space-x-2">
                            <div className="flex items-center flex-1 min-w-0 space-x-2">
                              <div className={`${dotAClass} flex-shrink-0`}></div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-1">
                                  <span className="text-sm font-black text-gray-900">{aPercent}%</span>
                                </div>
                                <span className="text-xs font-medium text-gray-600 bg-white px-2 py-0.5 rounded-full border border-gray-200 inline-block mt-1">
                                  {formatNumberWithCommas(a.value)}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center justify-end flex-1 min-w-0 space-x-2">
                              <div className="flex-1 min-w-0 text-right">
                                <div className="flex items-center justify-end space-x-1">
                                  <span className="text-sm font-black text-gray-900">{bPercent}%</span>
                                </div>
                                <span className="text-xs font-medium text-gray-600 bg-white px-2 py-0.5 rounded-full border border-gray-200 inline-block mt-1">
                                  {formatNumberWithCommas(b.value)}
                                </span>
                              </div>
                              <div className={`${dotBClass} flex-shrink-0`}></div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            ))}
          </motion.div>
        </AnimatePresence>

        {/* Additional charts only for Morning and only if extraData exists */}
        {activeSection === 'Morning' && extraData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="mt-16 space-y-8"
          >
            {/* Delay Reasons */}
            <div className="overflow-hidden border shadow-2xl bg-white/90 backdrop-blur-sm border-gray-200/50 rounded-3xl">
              <div className="relative p-8 bg-gradient-to-br from-amber-500 via-orange-500 to-red-500">
                <div className="absolute inset-0 bg-gradient-to-r from-yellow-600/20 to-red-600/20"></div>
                <div className="relative flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-white/20 backdrop-blur-sm rounded-2xl">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white">Top Delay Reasons</h3>
                      <p className="font-medium text-white/80">Factors causing polling delays</p>
                    </div>
                  </div>
                  <div className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-xl">
                    <span className="text-sm font-semibold text-white">
                      {extraData?.delay_reasons?.length || 0} Reason Types
                    </span>
                  </div>
                </div>
              </div>
              <div className="p-8">
                {extraData?.delay_reasons && extraData.delay_reasons.length > 0 ? (
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={extraData.delay_reasons} layout="vertical" margin={{ left: 20, right: 30, top: 20, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis type="number" tick={{ fontSize: 12, fill: '#6B7280' }} />
                      <YAxis dataKey="reason" type="category" width={180} tick={{ fontSize: 12, fill: '#374151' }} interval={0} />
                      <Bar dataKey="count" fill="url(#delayGradient)" radius={[0, 8, 8, 0]} stroke="#F59E0B" strokeWidth={1} />
                      <defs>
                        <linearGradient id="delayGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#FCD34D" />
                          <stop offset="100%" stopColor="#F59E0B" />
                        </linearGradient>
                      </defs>
                      <ReTooltip
                        formatter={(value: number) => [formatNumberWithCommas(value), 'Reports']}
                        contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #e5e7eb', borderRadius: '12px', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)', backdropFilter: 'blur(10px)' }}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="p-4 mb-4 bg-orange-100 rounded-full">
                      <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h4 className="mb-2 text-lg font-semibold text-gray-800">No Delay Issues Reported</h4>
                    <p className="max-w-sm text-gray-600">
                      Great news! No polling station delays have been reported for this period.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Resources */}
            <div className="overflow-hidden border shadow-2xl bg-white/90 backdrop-blur-sm border-gray-200/50 rounded-3xl">
              <div className="relative p-8 bg-gradient-to-br from-blue-500 via-indigo-600 to-purple-600">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20"></div>
                <div className="relative flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-white/20 backdrop-blur-sm rounded-2xl">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v8a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white">Voting Materials Availability</h3>
                      <p className="font-medium text-white/80">Essential resources at polling stations</p>
                    </div>
                  </div>
                  <div className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-xl">
                    <span className="text-sm font-semibold text-white">
                      {Object.keys(extraData?.resources || {}).length} Resources
                    </span>
                  </div>
                </div>
              </div>
              <div className="p-8">
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={resourceData} margin={{ left: 20, right: 30, top: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#374151' }} angle={-45} textAnchor="end" height={80} />
                    <YAxis tick={{ fontSize: 12, fill: '#6B7280' }} />
                    <Bar dataKey="count" fill="url(#resourceGradient)" radius={[8, 8, 0, 0]} stroke="#3B82F6" strokeWidth={1} />
                    <defs>
                      <linearGradient id="resourceGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#60A5FA" />
                        <stop offset="100%" stopColor="#3B82F6" />
                      </linearGradient>
                    </defs>
                    <ReTooltip
                      formatter={(value: number) => [formatNumberWithCommas(value), 'Available']}
                      contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #e5e7eb', borderRadius: '12px', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)', backdropFilter: 'blur(10px)' }}
                    />
                    <Legend wrapperStyle={{ fontSize: '14px', fontWeight: 500, color: '#374151' }} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default EdayPageGraphs;
